import { useState } from 'react'

import LoadingSpinner from '@components/LoadingSpinner'
import Button from '@components/designSystem/Button'
import FileUpload from '@components/designSystem/FileUpload'
import { Headline } from '@components/designSystem/Headline'
import { DatePicker } from '@components/designSystem/date-picker/date-picker'
import { useScan } from '@hooks/useScan.ts'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import { useInventoryUpload } from '@state/store.ts'
import { nativeToAriaDate } from '@utils/dates.ts'

import type { DateValue } from 'react-aria-components'

function StoreInventoryUpload() {
  const [file, setFile] = useState<File | null>(null)

  const { t } = useTypedTranslation()
  const { scanDate: currentScanDate } = useScan()
  const parsedCurrentScanDate: DateValue = nativeToAriaDate(currentScanDate)
  const [selectedDate, setSelectedDate] = useState<DateValue>(parsedCurrentScanDate)

  const uploadMutation = useInventoryUpload()

  function onChange(date: DateValue | null) {
    if (!date) return
    setSelectedDate(date)
  }

  async function handleUpload() {
    if (!file) return

    uploadMutation.mutate(
      { file, date: selectedDate },
      {
        onSuccess: () => setFile(null),
        onSettled: () => {
          setTimeout(() => uploadMutation.reset(), 2000)
        },
      }
    )
  }

  function handleFileSelect(selected: File | null) {
    setFile(selected)
    uploadMutation.reset()
  }

  return (
    <div className='m-auto w-1/2 max-w-100'>
      <Headline title={t('storeManagement.inventoryUpload')} />

      <div className="my-4 flex w-auto flex-row items-center gap-4">
        {t('storeManagement.date')}:
        <DatePicker
          aria-label={t('storeManagement.date')}
          value={selectedDate}
          onChange={onChange}
          maxValue={nativeToAriaDate(new Date())}
        />
      </div>

      <FileUpload accept=".csv" onChange={handleFileSelect} className="mb-4">
        {file ?
          <span className="text-sm font-medium">{file.name}</span> :
          <span className="text-sm text-gray-600">{t('storeManagement.clickHere')}</span>
        }
      </FileUpload>

      <div className='flex items-center gap-4'>
        <Button
          variant='filled'
          onClick={handleUpload}
          isDisabled={!file || uploadMutation.isPending}
        >
          {uploadMutation.isPending ? t('storeManagement.uploading') : t('storeManagement.upload')}
        </Button>

        {uploadMutation.isPending && <LoadingSpinner />}

        {uploadMutation.isSuccess && (
          <div className="text-sm text-green-600" data-testid="upload-result">
            {t('storeManagement.uploadSuccess')}
          </div>
        )}

        {uploadMutation.error && (
          <div className="text-sm text-red-600" data-testid="upload-result">
            {uploadMutation.error.message}
          </div>
        )}
      </div>
    </div>
  )
}

export default StoreInventoryUpload